import React, { useState, useEffect } from 'react';
import { Clock, Trash2, RefreshCw, CheckCircle, AlertCircle, Calendar, Video, ExternalLink } from 'lucide-react';
import './ScheduledPostsList.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const ScheduledPostsList = ({ session, isConnected }) => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isConnected && session?.access_token) {
            fetchScheduledPosts();
        } else {
            setLoading(false);
        }
    }, [isConnected, session]);

    const fetchScheduledPosts = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/api/youtube/scheduled`, {
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setPosts(data.posts || []);
            } else {
                throw new Error(data.error || 'Failed to fetch scheduled posts');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (postId) => {
        if (!window.confirm('Are you sure you want to cancel this scheduled upload?')) return;

        try {
            const res = await fetch(`${API_BASE}/api/youtube/scheduled/${postId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${session.access_token}` }
            });
            if (res.ok) {
                setPosts(prev => prev.filter(p => p.id !== postId));
            }
        } catch (err) {
            console.error('Failed to cancel post:', err);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status, error) => {
        switch (status) {
            case 'pending':
                return <span className="status-badge pending"><Clock size={12} /> Scheduled</span>;
            case 'processing': // Fixed mapping for 'uploading' if backend uses 'processing'
            case 'uploading':
                return <span className="status-badge uploading"><RefreshCw size={12} className="spin" /> Uploading</span>;
            case 'completed': // Backend uses 'completed'
            case 'complete':
                return <span className="status-badge complete"><CheckCircle size={12} /> Uploaded</span>;
            case 'failed':
                return (
                    <span
                        className="status-badge failed"
                        title={error || 'Upload failed'}
                        style={{ cursor: 'help' }}
                    >
                        <AlertCircle size={12} /> Failed
                    </span>
                );
            default:
                return <span className="status-badge">{status}</span>;
        }
    };

    if (!isConnected) {
        return null;
    }

    if (loading) {
        return (
            <div className="scheduled-posts-loading">
                <RefreshCw size={24} className="spin" />
                <span>Loading scheduled posts...</span>
            </div>
        );
    }

    return (
        <div className="scheduled-posts-container">
            <div className="posts-header">
                <h3>
                    <Calendar size={18} />
                    Scheduled Uploads
                </h3>
                <button className="refresh-btn" onClick={fetchScheduledPosts}>
                    <RefreshCw size={16} />
                </button>
            </div>

            {error && (
                <div className="posts-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {posts.length === 0 ? (
                <div className="empty-posts">
                    <Video size={40} />
                    <p>No scheduled uploads</p>
                    <span>Schedule your first upload above!</span>
                </div>
            ) : (
                <div className="posts-list">
                    {posts.map(post => (
                        <div key={post.id} className="post-item">
                            <div className="post-details">
                                <h4>{post.title}</h4>
                                <div className="post-meta">
                                    <span className="schedule-time">
                                        <Clock size={12} />
                                        {formatDate(post.scheduled_time)}
                                    </span>
                                    {getStatusBadge(post.status, post.error)}
                                </div>
                            </div>
                            <div className="post-actions">
                                {post.video_id && (
                                    <a
                                        href={`https://youtube.com/watch?v=${post.video_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="view-btn"
                                    >
                                        <ExternalLink size={14} />
                                    </a>
                                )}
                                {post.status !== 'completed' && (
                                    <button
                                        className="cancel-btn"
                                        onClick={() => handleCancel(post.id)}
                                        title={
                                            post.status === 'failed' ? 'Remove failed upload' :
                                                post.status === 'processing' ? 'Cancel stuck upload' :
                                                    'Cancel schedule'
                                        }
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
